/**
 * Mini-games — 4 per hero, each with multiple LEVELS (5–8) so kids can
 * play for an hour. Each game ties to a mental-health Protective Factor.
 *
 * Level shape per game type:
 *  - quiz     : { questions: [{q, options:[{...,correct}]}], points }
 *  - match    : { pairs: [{a,b}], points }
 *  - tap      : { targets: [{..., good}], seconds, points }
 *  - sequence : { colors:[...], maxRound, points }
 *  - breath   : { rounds, inhale, exhale, points }
 *
 * Levels are intentionally additive: each level reuses prior content plus
 * harder/longer additions so a kid can replay all 5+ levels in one sitting
 * without seeing the same script twice.
 */
import { GAME_CONTENT } from "./gameContent";

export const PROTECTIVE_FACTORS = {
  emotional_awareness: { en: "Emotional Awareness", es: "Conciencia emocional" },
  coping_skills: { en: "Coping Skills", es: "Afrontamiento" },
  problem_solving: { en: "Problem Solving", es: "Resolución de problemas" },
  social_connection: { en: "Social Connection", es: "Conexión social" },
  purpose_confidence: { en: "Purpose & Confidence", es: "Propósito y confianza" },
};

const buildQuizLevels = (questionsByLevel, basePoints) =>
  questionsByLevel.map((qs, idx) => ({
    name: { en: `Level ${idx + 1}`, es: `Nivel ${idx + 1}` },
    questions: qs,
    points: basePoints + idx * 10,
  }));

const buildMatchLevels = (pairsByLevel, basePoints) =>
  pairsByLevel.map((pairs, idx) => ({
    name: { en: `Level ${idx + 1}`, es: `Nivel ${idx + 1}` },
    pairs,
    points: basePoints + idx * 10,
  }));

const buildTapLevels = (targetsByLevel, basePoints) =>
  targetsByLevel.map((targets, idx) => ({
    name: { en: `Level ${idx + 1}`, es: `Nivel ${idx + 1}` },
    targets,
    seconds: 22 + idx * 5,           // 22s → 47s
    points: basePoints + idx * 10,
  }));

const buildSequenceLevels = (paletteVariants, basePoints) =>
  paletteVariants.map((colors, idx) => ({
    name: { en: `Level ${idx + 1}`, es: `Nivel ${idx + 1}` },
    colors,
    maxRound: 5 + idx,               // 5 → 11
    points: basePoints + idx * 10,
  }));

const buildBreathLevels = (configs, basePoints) =>
  configs.map((cfg, idx) => ({
    name: { en: `Level ${idx + 1}`, es: `Nivel ${idx + 1}` },
    ...cfg,
    points: basePoints + idx * 10,
  }));

export const GAMES = {
  queen: [
    {
      id: "q-crown-compliment",
      hero: "queen",
      type: "quiz",
      protective_factor: "purpose_confidence",
      points: 50,
      title: { en: "Crown the Compliment", es: "Corona el cumplido" },
      desc: {
        en: "Spot the affirmation that lifts you up — not the one that puts you down.",
        es: "Encuentra la afirmación que te eleva — no la que te baja.",
      },
      levels: buildQuizLevels(GAME_CONTENT.q_crown_compliment, 40),
    },
    {
      id: "q-mirror-match",
      hero: "queen",
      type: "match",
      protective_factor: "emotional_awareness",
      points: 60,
      title: { en: "Mirror Match", es: "Coincidencia en el espejo" },
      desc: {
        en: "Match each negative thought with its Queen-style reframe.",
        es: "Empareja cada pensamiento negativo con su reformulación de Reina.",
      },
      levels: buildMatchLevels(GAME_CONTENT.q_mirror_match, 50),
    },
    {
      id: "q-affirm-streak",
      hero: "queen",
      type: "tap",
      protective_factor: "purpose_confidence",
      points: 50,
      title: { en: "Affirmation Streak", es: "Racha de afirmaciones" },
      desc: {
        en: "Tap only the kind, true affirmations. Skip the put-downs.",
        es: "Toca solo las afirmaciones amables y verdaderas. Salta los insultos.",
      },
      levels: buildTapLevels(GAME_CONTENT.q_affirm_streak, 40),
    },
    {
      id: "q-power-quiz",
      hero: "queen",
      type: "quiz",
      protective_factor: "purpose_confidence",
      points: 50,
      title: { en: "Power-Word Quiz", es: "Quiz de palabras de poder" },
      desc: {
        en: "Pick the power-word that fits each moment.",
        es: "Elige la palabra de poder para cada momento.",
      },
      levels: buildQuizLevels(GAME_CONTENT.q_power_quiz, 40),
    },
  ],

  victor: [
    {
      id: "v-speak-up",
      hero: "victor",
      type: "quiz",
      protective_factor: "social_connection",
      points: 50,
      title: { en: "Speak-Up Scenarios", es: "Escenarios para alzar la voz" },
      desc: {
        en: "Pick the healthiest way to use your voice.",
        es: "Elige la forma más sana de usar tu voz.",
      },
      levels: buildQuizLevels(GAME_CONTENT.v_speak_up, 40),
    },
    {
      id: "v-help-harm",
      hero: "victor",
      type: "tap",
      protective_factor: "social_connection",
      points: 50,
      title: { en: "Help vs. Harm", es: "Ayuda vs. Daño" },
      desc: {
        en: "Tap the words that HELP a friend. Skip the words that hurt.",
        es: "Toca las palabras que AYUDAN. Salta las que dañan.",
      },
      levels: buildTapLevels(GAME_CONTENT.v_help_harm, 40),
    },
    {
      id: "v-listen-match",
      hero: "victor",
      type: "match",
      protective_factor: "social_connection",
      points: 60,
      title: { en: "Active Listening Match", es: "Coincidencia de escucha activa" },
      desc: {
        en: "Match what a friend says to the response that truly hears them.",
        es: "Empareja lo que dice un amigo con la respuesta que realmente lo escucha.",
      },
      levels: buildMatchLevels(GAME_CONTENT.v_listen_match, 50),
    },
    {
      id: "v-safe-person",
      hero: "victor",
      type: "quiz",
      protective_factor: "problem_solving",
      points: 50,
      title: { en: "Find Your Safe Person", es: "Encuentra a tu persona segura" },
      desc: {
        en: "Pick the person it's safe to tell hard truths to.",
        es: "Elige a la persona a la que es seguro contarle verdades difíciles.",
      },
      levels: buildQuizLevels(GAME_CONTENT.v_safe_person, 40),
    },
  ],

  cam: [
    {
      id: "c-breath-game",
      hero: "cam",
      type: "breath",
      protective_factor: "coping_skills",
      points: 50,
      title: { en: "Breath Wave", es: "Ola de respiración" },
      desc: {
        en: "Tap the wave at the peak inhale and the bottom exhale. Stay in sync to score.",
        es: "Toca la ola en lo alto de la inhalación y en lo bajo de la exhalación. Mantén el ritmo.",
      },
      levels: buildBreathLevels(GAME_CONTENT.c_breath_game, 40),
    },
    {
      id: "c-calm-sequence",
      hero: "cam",
      type: "sequence",
      protective_factor: "coping_skills",
      points: 60,
      title: { en: "Calm Sequence", es: "Secuencia de calma" },
      desc: {
        en: "Watch a calm sequence light up, then repeat it. Gets longer each round.",
        es: "Mira una secuencia tranquila iluminarse, luego repítela. Crece cada ronda.",
      },
      levels: buildSequenceLevels(GAME_CONTENT.c_calm_sequence, 50),
    },
    {
      id: "c-color-tap",
      hero: "cam",
      type: "tap",
      protective_factor: "emotional_awareness",
      points: 50,
      title: { en: "Color Calm Tap", es: "Toque de color calma" },
      desc: {
        en: "Tap only the COOL colors (blue, teal, violet). Skip the hot ones.",
        es: "Toca solo los colores FRÍOS (azul, verde, violeta). Salta los cálidos.",
      },
      levels: buildTapLevels(GAME_CONTENT.c_color_tap, 40),
    },
    {
      id: "c-coping-quiz",
      hero: "cam",
      type: "quiz",
      protective_factor: "coping_skills",
      points: 50,
      title: { en: "Coping Toolbox Quiz", es: "Quiz de caja de afrontamiento" },
      desc: {
        en: "Pick the healthier coping tool for each moment.",
        es: "Elige la herramienta más sana para cada momento.",
      },
      levels: buildQuizLevels(GAME_CONTENT.c_coping_quiz, 40),
    },
  ],
};

export const GAMES_FLAT = [...GAMES.queen, ...GAMES.victor, ...GAMES.cam];
